import { useRef } from 'react';
import type { Vendor, Category } from '../../../../types';
import { useEscClose } from '../../../../lib/a11y/useEscClose';
import { useFocusTrap } from '../../../../lib/a11y/useFocusTrap';
import { Scrollable } from '../../../../components/primitives/Scrollable';
import { VendorHero } from './VendorHero';
import { VendorDetailIdentity } from './VendorDetailIdentity';
import { VendorCategoryStrip } from './VendorCategoryStrip';
import { StageSchedule } from '../../../schedule/components/StageSchedule';
import shared from './shared.module.css';
import styles from './Mobile.module.css';

interface Props {
  vendor: Vendor;
  category?: Category | undefined;
  locationName?: string | undefined;
  onBack: () => void;
  onShowOnMap?: ((vendor: Vendor) => void) | undefined;
}

/**
 * Mobile full-screen vendor detail. Structure mirrors Desktop.tsx — same
 * category strip → 120px hero → centered identity → description → CTA —
 * just stretched vertically and with a sticky "Show on Map" footer that
 * desktop doesn't have.
 */
export function Mobile({ vendor, category, locationName, onBack, onShowOnMap }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Honor the role=dialog aria-modal=true contract: move focus into the
  // dialog on open and cycle Tab within it. If nothing inside is focusable,
  // the hook falls back to focusing the container.
  //
  // Restore-on-close is owned by AppShell, not this hook: opening the
  // full-screen detail unmounts the originating surface (the list row, or
  // the bottom sheet's "View details" button), so by the time this trap's
  // effect runs `document.activeElement` is already <body>. Restoring there
  // would strand the user. AppShell re-focuses the freshly-remounted row
  // once the detail closes (see restoreRowFocusRef).
  useFocusTrap(true, dialogRef);
  useEscClose(true, onBack);

  const story = vendor.description;

  return (
    <div
      ref={dialogRef}
      className={styles.detail}
      role="dialog"
      aria-modal="true"
      aria-label={vendor.name}
    >
      <VendorCategoryStrip vendor={vendor} category={category} onClose={onBack} />
      <VendorHero key={vendor.uid} vendor={vendor} category={category} />
      <VendorDetailIdentity vendor={vendor} locationName={locationName} />
      <Scrollable className={styles.scroll}>
        <div className={styles.body}>
          {story && <p className={shared.descriptionBlock}>{story}</p>}
          {vendor.type === 'stage' && locationName && (
            <StageSchedule stageLocation={locationName} />
          )}
        </div>
      </Scrollable>

      {onShowOnMap && (
        <div className={shared.stickyFooter}>
          <button type="button" className={shared.primaryCta} onClick={() => onShowOnMap(vendor)}>
            Show on Map
          </button>
        </div>
      )}
    </div>
  );
}
